import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/auth';
import { becomeMentor } from '@/services/roleService';
import { GraduationCap, Loader2, CheckCircle2, DollarSign, Users, BookOpen } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

export const BecomeMentorCard = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  const handleBecomeMentor = async () => {
    if (!user?.id) {
      toast({
        title: 'Erro',
        description: 'Usuário não encontrado. Faça login novamente.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const result = await becomeMentor(user.id);

      if (result.success) {
        // Invalidar todos os caches relacionados ao usuário
        queryClient.invalidateQueries({ queryKey: ['user-role'] });
        queryClient.invalidateQueries({ queryKey: ['profile'] });
        queryClient.invalidateQueries({ queryKey: ['user-profile'] });
        
        toast({
          title: 'Parabéns! 🎉',
          description: 'Você agora é um mentor! Redirecionando para o painel do mentor...',
        });

        // Forçar refresh completo da página para garantir que o role seja atualizado
        // Isso é necessário porque o useRole usa cache global que não é invalidado pelo React Query
        setTimeout(() => {
          window.location.href = '/painel-mentor';
        }, 1500);
      } else {
        toast({
          title: 'Erro',
          description: result.error || 'Não foi possível tornar-se mentor. Tente novamente.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Erro ao tornar-se mentor:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro inesperado. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="border-[#00B6F0]/20 bg-gradient-to-br from-[#0f141c] to-[#1a2233] shadow-lg">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#00B6F0]/10 rounded-lg">
            <GraduationCap className="w-6 h-6 text-[#00B6F0]" />
          </div>
          <div>
            <CardTitle className="text-white text-xl">Tornar-se Mentor</CardTitle>
            <CardDescription className="text-gray-400">
              Compartilhe seu conhecimento e ganhe dinheiro ensinando
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <p className="text-sm text-gray-300 font-medium">
            Como mentor, você poderá:
          </p>
          <ul className="text-sm text-gray-400 space-y-2">
            <li className="flex items-start gap-2">
              <BookOpen className="w-4 h-4 text-[#00B6F0] mt-0.5 flex-shrink-0" />
              <span>Criar mentorias personalizadas e definir seus próprios preços</span>
            </li>
            <li className="flex items-start gap-2">
              <Users className="w-4 h-4 text-[#00B6F0] mt-0.5 flex-shrink-0" />
              <span>Gerenciar comunidades e interagir com alunos</span>
            </li>
            <li className="flex items-start gap-2">
              <DollarSign className="w-4 h-4 text-[#00B6F0] mt-0.5 flex-shrink-0" />
              <span>Receber pagamentos pelos seus serviços e conteúdo</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#00B6F0] mt-0.5 flex-shrink-0" />
              <span>Construir sua reputação com avaliações de alunos</span>
            </li>
          </ul>
        </div>
        
        <div className="pt-2">
          <Button
            onClick={handleBecomeMentor}
            disabled={isLoading}
            className="w-full bg-[#00B6F0] hover:bg-[#00A3D9] text-black font-semibold transition-all"
            size="lg"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                <GraduationCap className="w-4 h-4 mr-2" />
                Tornar-me Mentor Agora
              </>
            )}
          </Button>
          <p className="text-xs text-gray-500 mt-2 text-center">
            Você poderá acessar todas as funcionalidades do painel do mentor imediatamente
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

